import _ from 'underscore';
import { get } from 'lodash';
import { useMemo } from 'react';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';

import { uiNextColors } from '../styles/color';
import { symbols } from '../styles/line';

export interface IDataPoint {
  DateTime: string;
  Value: number;
}
export interface IDataPointWithDate {
  DateTime: Date;
  Value: number;
}
export interface IPerformanceTimeSeries {
  UniqueId: string;
  MetricName: string;
  PrimaryDataPoints: IDataPoint[];
  ComparisonDataPoints: IDataPoint[];
}

interface ISeriesWithDate {
  MetricName: string;
  PrimaryDataPoints: Array<IDataPointWithDate>;
  ComparisonDataPoints: Array<IDataPointWithDate>;
}

function parseAPIDate(date, i18n, ianaTimeZone) {
  const parser = i18n.dateParser({
    raw: 'yyyy-MM-ddTHH:mm:ss',
    timeZone: ianaTimeZone,
  });

  return parser(date.substr(0, 19));
}


function transformPoints(points, i18n, ianaTimeZone): IDataPointWithDate[] {
  return points.map(({ DateTime, Value }) => {
    const parsedDate = parseAPIDate(DateTime, i18n, ianaTimeZone);
    return {
      DateTime: parsedDate,
      Value,
    };
  });
}

function getSeriesWithDate(performanceTimeSeries: IPerformanceTimeSeries[], i18n, ianaTimeZone): ISeriesWithDate[] {
  return performanceTimeSeries.map(({ MetricName, PrimaryDataPoints, ComparisonDataPoints }) => ({
    MetricName,
    PrimaryDataPoints: transformPoints(PrimaryDataPoints, i18n, ianaTimeZone),
    ComparisonDataPoints: transformPoints(ComparisonDataPoints, i18n, ianaTimeZone),
  }));
}

const mismatches = {
  Spend: 'Spent',
  CTR: 'ClickThruRate',
};

function coerceMetric(metric) {
  return get(mismatches, metric, metric);
}

const granularityOptions = {
  Hour: { datetime: 'short' },
  Day: { skeleton: 'yMEd' },
  // Week: { skeleton: 'MMMMW' },
  Week: { skeleton: 'yMMMd' }, // temporal fix for CLDR breaking change
  Month: { skeleton: 'yMMM' },
  Quarter: { skeleton: 'yQQQ' },
};


function formatDate(timestamp, granularity, i18n, TimeZone): string {
  const options = granularityOptions[granularity];

  if (timestamp && options) {
    const formattedDate = i18n.formatDate(new Date(timestamp), _.extend({
      timeZone: toIANATimeZone(fromLegacyTimeZone(TimeZone)),
    }, granularityOptions[granularity]));

    return granularity === 'Week' ? i18n.getString(_TL_('Week of {{date}}'), {
      date: formattedDate,
    }) : formattedDate;
  }

  return '';
}

function highchartsPoints(points: Array<{ DateTime: Date, Value: number }>, granularity: string, i18n, TimeZone: number): Array<[string, number]> {
  return points.map(point => [formatDate(point.DateTime.getTime(), granularity, i18n, TimeZone), point.Value]);
}


export interface ISeries {
  category: string;
  name: string;
  isOriginal?: boolean;
  isComparison?: boolean;
  color: string;
  data: Array<[string, number]>;
  lineWidth: number;
  dashStyle: string;
  marker: {
    enabled: boolean;
    fillColor: string;
    lineColor: string;
    lineWidth: number;
    radius: number;
    symbol: string;
  };
  yAxis: number;
  xAxis?: number;
}

function highchartsSeries(series: ISeriesWithDate[], metric, metricDisplayName, colors, symbol, granularity, i18n, TimeZone) : ISeries[] {
  if (_.isEmpty(metric) || metric === 'None') {
    return [];
  }

  const { PrimaryDataPoints, ComparisonDataPoints } = _.find(series, ({ MetricName }) => MetricName === coerceMetric(metric)) || {};
  const category = metric;

  const marker = {
    enabled: true,
    fillColor: colors.original,
    lineColor: '#FFFFFF',
    lineWidth: 2,
    radius: 6,
    symbol,
  };

  return [
    ..._.isEmpty(PrimaryDataPoints) ? [] : [{
      category,
      name: metricDisplayName,
      isOriginal: true,
      color: colors.original,
      data: highchartsPoints(PrimaryDataPoints, granularity, i18n, TimeZone),
      lineWidth: 2,
      dashStyle: 'Solid',
      marker,
      yAxis: 0,
    }],
    ..._.isEmpty(ComparisonDataPoints) ? [] : [{
      category,
      name: metricDisplayName,
      isComparison: true,
      color: colors.comparative,
      data: highchartsPoints(ComparisonDataPoints, granularity, i18n, TimeZone),
      lineWidth: 2,
      dashStyle: 'ShortDash',
      marker,
      xAxis: 1,
      yAxis: 0,
    }],
  ];
}

export function useChartData({
  performanceTimeSeries,
  i18n,
  selectedMetricIndexes,
  visibleMetrics,
  visibleMetricsDisplayNames,
  granularity,
  TimeZone,
}: {
  performanceTimeSeries: IPerformanceTimeSeries[];
  i18n: any;
  selectedMetricIndexes: number[];
  visibleMetrics: string[];
  visibleMetricsDisplayNames: string[];
  granularity: string;
  TimeZone: number;
}) : ISeries[] {
  const result = useMemo(() => {
    const ianaTimeZone = toIANATimeZone(fromLegacyTimeZone(TimeZone));
    const seriesWithDate = getSeriesWithDate(performanceTimeSeries, i18n, ianaTimeZone);

    const chartSeries = _.map(selectedMetricIndexes, (metricIndex, index) => {
      const colors = uiNextColors;
      const symbol = symbols[metricIndex];
      const series = highchartsSeries(seriesWithDate, visibleMetrics[metricIndex], visibleMetricsDisplayNames[metricIndex], colors[metricIndex], symbol, granularity, i18n, TimeZone);

      _.each(series, (data) => {
        // eslint-disable-next-line no-param-reassign
        data.yAxis = index;
      });
      return series;
    });

    return _.flatten(chartSeries);
  }, [performanceTimeSeries, i18n, selectedMetricIndexes, visibleMetrics, visibleMetricsDisplayNames, granularity, TimeZone]);

  return result;
}
